import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth/auth.service';
import { SessionExpiryService } from '../services/session/session-expiry.service';
import { TenantContextService } from '../services/tenant/tenant-context.service';

const AUTH_URLS = [
  environment.user.auth,
  environment.user.register,
  environment.user.refresh,
  environment.user.logout,
  environment.user.validate,
];

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<boolean | null>(null);

  constructor(
    private authService: AuthService,
    private tenantContext: TenantContextService,
    private sessionExpiry: SessionExpiryService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const tenantGuid = this.tenantContext.getTenantGuid();
    const headers: Record<string, string> = {};
    if (tenantGuid) {
      headers['X-Tenant-Guid'] = tenantGuid;
    }

    const cloned = req.clone({
      withCredentials: true,
      setHeaders: headers,
    });

    return next.handle(cloned).pipe(
      catchError(err => {
        const isHrisApi = cloned.url.startsWith(environment.api_base_url);
        const isAuthEndpoint = AUTH_URLS.some(url => cloned.url.startsWith(url));
        const isSalaryReveal = cloned.url.includes('/salary-reveal');
        if (
          isHrisApi &&
          err instanceof HttpErrorResponse &&
          err.status === 401 &&
          !isAuthEndpoint &&
          !isSalaryReveal
        ) {
          return this.handle401(cloned, next);
        }
        return throwError(() => err);
      })
    );
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler) {
    if (this.authService.isLogoutInProgress()) {
      return throwError(() => new Error('Logout in progress'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);

      return this.authService.refreshToken().pipe(
        timeout(15000),
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshSubject.next(true);
          return next.handle(request);
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshSubject.next(false);
          this.sessionExpiry.markExpired();
          return throwError(() => err);
        })
      );
    }

    return this.refreshSubject.pipe(
      filter(x => x !== null),
      take(1),
      switchMap((ok) => {
        if (!ok) {
          return throwError(() => new HttpErrorResponse({
            status: 401,
            url: request.url,
            statusText: 'Unauthorized',
          }));
        }
        return next.handle(request);
      })
    );
  }
}
